/**
 * Get Analytics Use Case
 * Fetches energy and revenue analytics for a date range
 */

import { DashboardRepository } from '../data/dashboard-repository';
import {
  AnalyticsDateRange,
  EnergyDataPoint,
  RevenueDataPoint,
  AnalyticsSummary,
  TopLocation,
} from '../types/dashboard-types';

export class GetAnalyticsUseCase {
  constructor(private readonly repository: DashboardRepository) {}

  async execute(
    dateRange: AnalyticsDateRange,
    topLocationsLimit?: number,
    topLocationsSortBy?: 'revenue' | 'energy' | 'sessions'
  ): Promise<{
    energyData: EnergyDataPoint[];
    revenueData: RevenueDataPoint[];
    summary: AnalyticsSummary;
    topLocations: TopLocation[];
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      // Fetch all analytics data in parallel
      const [energyResult, revenueResult, topLocations] = await Promise.all([
        this.repository.getEnergyAnalytics(dateRange),
        this.repository.getRevenueAnalytics(dateRange),
        this.repository.getTopLocations(dateRange, topLocationsLimit ?? 10, topLocationsSortBy ?? 'revenue'),
      ]);

      // Combine summaries from energy and revenue results
      const summary: AnalyticsSummary = {
        totalEnergy: energyResult.summary.totalEnergy ?? 0,
        totalRevenue: revenueResult.summary.totalRevenue ?? 0,
        totalSessions: energyResult.summary.totalSessions ?? 0,
        averageSessionDuration: this.calculateAverageSessionDuration(energyResult.dataPoints),
        averageEnergyPerSession: energyResult.summary.averageEnergyPerSession ?? 0,
        averageRevenuePerSession: revenueResult.summary.averageRevenuePerSession ?? 0,
        peakPowerKw: energyResult.summary.peakPowerKw ?? 0,
        energyChangePercent: energyResult.summary.energyChangePercent ?? 0,
        revenueChangePercent: revenueResult.summary.revenueChangePercent ?? 0,
      };

      return {
        energyData: energyResult.dataPoints,
        revenueData: revenueResult.dataPoints,
        summary,
        topLocations,
        hasError: false,
      };
    } catch (error) {
      console.error('GetAnalyticsUseCase.execute failed:', error);

      return {
        energyData: [],
        revenueData: [],
        summary: this.getEmptySummary(),
        topLocations: [],
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load analytics',
      };
    }
  }

  /**
   * Calculate average session duration from energy data
   * Estimates based on energy and typical charging power
   */
  private calculateAverageSessionDuration(energyData: EnergyDataPoint[]): number {
    if (energyData.length === 0) return 0;

    const totalSessions = energyData.reduce((sum, d) => sum + d.sessionsCount, 0);
    const totalEnergy = energyData.reduce((sum, d) => sum + d.energyKwh, 0);

    if (totalSessions === 0) return 0;

    // Estimate based on average 20 kWh charging power
    const averageEnergyPerSession = totalEnergy / totalSessions;
    const averagePowerKw = 20; // Typical average charging power
    const averageDurationHours = averageEnergyPerSession / averagePowerKw;

    return Math.round(averageDurationHours * 60); // Convert to minutes
  }

  private getEmptySummary(): AnalyticsSummary {
    return {
      totalEnergy: 0,
      totalRevenue: 0,
      totalSessions: 0,
      averageSessionDuration: 0,
      averageEnergyPerSession: 0,
      averageRevenuePerSession: 0,
      peakPowerKw: 0,
      energyChangePercent: 0,
      revenueChangePercent: 0,
    };
  }
}
